import React, { useState } from 'react';
import { Card, Center, Container, Stack } from '@fiverr-private/layout_components';
import { DynamicBriefL2Icon, MinimizeIcon, PlayCircleSolidIcon } from '@fiverr-private/visuals';
import { Heading, Text } from '@fiverr-private/typography';
import { IconButton, Button } from '@fiverr-private/button';
import { useOpenBriefCreationDrawer } from '@fiverr-private/dm_brief_creation_triggers';
import { AppI18n } from '../../i18n';
import { HowToModal } from '../HowToModal';
import { Currency } from '../../types/currency';
import { BffPopupElementNames, reportClickedBriefFloatingFormPopupEvent } from '../../monitoring/bqEvents';
import { textWithBottomOpacity } from './style.ve.css';
import { BffPopupTtile } from './BffPopupTitle';
import { TriggerScenario } from './types';

export interface BriefFloatingFormPopupProps {
  triggerScenario?: TriggerScenario;
  initialPromptAndProfession: { initialPrompt: string; profession: string };
  setIsPopupOpen: (isOpen: boolean) => void;
  currency: Currency;
  pageName: string;
  onEditBriefClick?: () => void;
}

export const BriefFloatingFormPopup = ({
  triggerScenario,
  initialPromptAndProfession,
  setIsPopupOpen,
  currency,
  pageName,
  onEditBriefClick,
}: React.PropsWithChildren<BriefFloatingFormPopupProps>) => {
  const [isLoadingBriefCreationDrawer, setIsLoadingBriefCreationDrawer] = useState(false);
  const { openBriefCreationDrawer } = useOpenBriefCreationDrawer();

  const onCloseClick = () => {
    setIsPopupOpen(false);

    reportClickedBriefFloatingFormPopupEvent(pageName, BffPopupElementNames.CLOSE_POPUP);
  };

  const onEditClick = () => {
    setIsLoadingBriefCreationDrawer(true);

    openBriefCreationDrawer({
      currency,
      source: `bff_${pageName}`,
      initialPrompt: initialPromptAndProfession.initialPrompt,
      onOpen: () => {
        setIsPopupOpen(false);
        setIsLoadingBriefCreationDrawer(false);
      },
    });

    onEditBriefClick?.();

    reportClickedBriefFloatingFormPopupEvent(pageName, BffPopupElementNames.EDIT_YOUR_BRIEF);
  };

  return (
    <Card
      background="white"
      dropShadow="z2"
      borderRadius="xl"
      maxWidth="345px"
      maxHeight="498px"
      borderColor="grey_400"
    >
      <Stack justifyContent="flexEnd" paddingTop="4" paddingX="6">
        <IconButton aria-label="Like" variant="ghost" size="xs" onClick={onCloseClick}>
          <MinimizeIcon color="grey_900" />
        </IconButton>
      </Stack>
      <Stack direction="column" padding="6" paddingTop="0" gap="6">
        <Center>
          <DynamicBriefL2Icon size="lg" />
        </Center>
        <Stack direction="column" gap="6">
          <Stack direction="column" gap="2">
            <Heading as="h5">
              <BffPopupTtile triggerScenario={triggerScenario} profession={initialPromptAndProfession.profession} />
            </Heading>
            <HowToModal>
              <Stack
                cursor="pointer"
                alignItems="center"
                gap="2"
                display="inlineFlex"
                onClick={() =>
                  reportClickedBriefFloatingFormPopupEvent(pageName, BffPopupElementNames.SEE_HOW_IT_WORKS)
                }
              >
                <PlayCircleSolidIcon color="grey_1200" />
                <Text size="b_sm" paddingTop="0.5">
                  <AppI18n k="brief_floating_form.popup.see_how_it_works" />
                </Text>
              </Stack>
            </HowToModal>
          </Stack>
          <Stack direction="column" backgroundColor="grey_200" borderRadius="xl" padding="4">
            <Container className={textWithBottomOpacity}>
              <Text
                display="webkitBox"
                overflow="hidden"
                textOverflow="ellipsis"
                webkitBoxOrient="vertical"
                webkitLineClamp={4}
              >
                {initialPromptAndProfession.initialPrompt}
              </Text>
            </Container>
            <Button size="sm" loading={isLoadingBriefCreationDrawer} onClick={onEditClick}>
              <AppI18n k="brief_floating_form.popup.cta" />
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
